import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Column } from 'react-table'

import withNavigationBar from 'hoc/nav-bar/with-navigation-bar'
import { AdvancedTable, AdvTableSearchComponent, Button, ErrorView, Flex } from 'components'
import Filter from 'components/AdvancedTable/Filters'
import AccessDenied from 'modules/shared/AccessDenied'

import { ItemResponse, GetListPayload } from 'services/quotation-pending-approval'
import { useQuotationPendingApprovalList } from 'queries/quotation-pending-approval'
import { formatDatetime } from 'utils/datetime'
import useUser from 'hooks/use-user'
import { getLeadRoute } from 'utils/url'
import { useReusableTab } from 'hooks/use-reusable-lead-tab'
import { ROUTES } from '../../../constants/constants'

export const url = `/${ROUTES.OPPORTUNITIES_QUOTATIONS_APPROVAL}`

// The API sends plain English filter titles; map them to translation keys by filter key.
// A key without a translation falls back to the API title.
const FILTER_TITLE_KEYS: Record<string, string> = {
  created_by: 'submitted_by_lbl',
  contract_length: 'contract_length_lbl',
}

const PendingApprovalQuotation = () => {
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const { openInReusableTab } = useReusableTab()
  const { currentQuotationApprovalLevel } = useUser()

  const [payload, setPayload] = useState<GetListPayload>({
    page: 1,
    per_page: 100,
    sort_by: null,
  })

  const { data, isLoading, isError, error } = useQuotationPendingApprovalList(payload)

  const handleRowClick = (row: ItemResponse) => {
    const leadUrl = `${getLeadRoute({
      leadId: row.lead_id,
    })}?window=opportunities-tab&window_type=quotation&quotation_id=${row.quotation_id}`
    openInReusableTab(leadUrl)
  }

  if (!currentQuotationApprovalLevel || currentQuotationApprovalLevel === 0) {
    return <AccessDenied />
  }

  if (isError) {
    return <ErrorView error={error} />
  }

  return (
    <AdvancedTable<ItemResponse>
      customHeader={
        <Flex p="0.5rem">
          <Button variant="ghost" icon={['far', 'angle-left']} onClick={() => navigate(`/${ROUTES.OPPORTUNITIES}`)}>
            opportunity_lbl
          </Button>
        </Flex>
      }
      filters={
        <Filter
          filters={data?.filters?.map((filter) => {
            const titleKey = FILTER_TITLE_KEYS[filter.key]
            return { ...filter, title: titleKey && i18n.exists(titleKey) ? titleKey : filter.title }
          })}
          values={payload.filter}
          onChange={(selected) => setPayload((prev) => ({ ...prev, filter: selected }))}
          isLoading={isLoading}
        />
      }
      searchBar={
        <AdvTableSearchComponent setSearchValue={(search) => setPayload((prev) => ({ ...prev, query: search }))} />
      }
      uniqueTableName="pending-approval-quotation"
      columns={columns}
      data={data?.data ?? []}
      totalDataCount={data?.total_count ?? 0}
      onRowClick={handleRowClick}
      isLoading={isLoading}
      isResponsiveHeight
      serverSide
      pageNumber={payload.page}
      pageSize={payload.per_page}
      onChangePagination={(page) => setPayload((prev) => ({ ...prev, page: page.pageNumber, per_page: page.pageSize }))}
      onSort={(sortKey, order) =>
        setPayload((prev) => ({
          ...prev,
          sort_by:
            order === 'default'
              ? null
              : {
                  key: sortKey,
                  order: order,
                },
        }))
      }
    />
  )
}

const columns: Column<ItemResponse>[] = [
  {
    Header: 'quotation_account_name_lbl',
    accessor: 'lead_name',
  },
  {
    Header: 'quotation_reference_number_lbl',
    accessor: 'reference_number',
  },
  {
    Header: 'contract_length_lbl',
    accessor: 'contract_length',
  },
  {
    Header: 'approval_status_lbl',
    accessor: 'approval_status',
  },
  {
    Header: 'submitted_at_lbl',
    accessor: 'create_ts',
    Cell: ({ value }) => formatDatetime(value),
  },
  {
    Header: 'submitted_by_lbl',
    accessor: 'created_by_name',
  },
]

export default withNavigationBar(PendingApprovalQuotation)
